// ─────────────────────────────────────────────────────────────────────────────
// Demo Mode Utility
// All content here is ONLY used when the logged-in user is demo@cruvzmail.com.
// No other user is affected.
// ─────────────────────────────────────────────────────────────────────────────

export const DEMO_EMAIL = 'demo@cruvzmail.com';

export function isDemoUser(email?: string | null): boolean {
  return email === DEMO_EMAIL;
}

// ─────────── Chat / Streams ───────────

const D4 = '2026-05-05T14:30:00.000Z'; // 4 days ago stale date
const D3 = '2026-05-06T09:15:00.000Z';
const D2 = '2026-05-07T11:45:00.000Z';
const D1 = '2026-05-08T16:20:00.000Z';

export const DEMO_CHANNELS = [
  { id: 1001, name: 'general', description: 'Company-wide announcements', type: 'public' as const, isPrivate: false, isDefault: true, createdBy: 'admin@cruvzmail.com', createdAt: D4 },
  { id: 1002, name: 'announcements', description: 'Official announcements only', type: 'announcement' as const, isPrivate: false, isDefault: false, createdBy: 'admin@cruvzmail.com', createdAt: D4 },
  { id: 1003, name: 'engineering', description: 'Engineering team', type: 'private' as const, isPrivate: true, isDefault: false, createdBy: 'admin@cruvzmail.com', createdAt: D4 },
];

export const DEMO_DM_ROOMS = [
  {
    id: 2001,
    otherUser: { id: 'u-alice', email: 'alice@cruvzmail.com', firstName: 'Alice', lastName: 'Johnson', domain: 'cruvzmail.com', status: 'available' },
    lastMessage: { content: 'Sounds good, see you then!', messageType: 'text', createdAt: D2, senderId: 'u-alice' },
    unreadCount: 0,
  },
  {
    id: 2002,
    otherUser: { id: 'u-bob', email: 'bob@cruvzmail.com', firstName: 'Bob', lastName: 'Smith', domain: 'cruvzmail.com', status: 'busy' },
    lastMessage: { content: "I'll review the report tonight.", messageType: 'text', createdAt: D3, senderId: 'u-bob' },
    unreadCount: 2,
  },
  {
    id: 2003,
    otherUser: { id: 'u-carol', email: 'carol@cruvzmail.com', firstName: 'Carol', lastName: 'Williams', domain: 'cruvzmail.com', status: 'away' },
    lastMessage: { content: 'Approved ✅', messageType: 'text', createdAt: D1, senderId: 'u-carol' },
    unreadCount: 0,
  },
];

const makeChatMsg = (
  id: number,
  email: string,
  firstName: string,
  lastName: string,
  content: string,
  createdAt: string,
  channelId?: number,
  roomId?: number,
) => ({
  id,
  content,
  messageType: 'text' as const,
  createdAt,
  isEdited: false,
  isDeleted: false,
  isPinned: false,
  isPriority: false,
  channelId,
  roomId,
  reactions: [],
  readBy: [],
  sender: { id: `u-${email.split('@')[0]}`, email, firstName, lastName },
});

export const DEMO_CHANNEL_MESSAGES: Record<number, ReturnType<typeof makeChatMsg>[]> = {
  1001: [
    makeChatMsg(3001, 'alice@cruvzmail.com', 'Alice', 'Johnson', 'Welcome to Cruvzmail! 👋 Feel free to explore everything.', D4, 1001),
    makeChatMsg(3002, 'bob@cruvzmail.com', 'Bob', 'Smith', 'Great to have everyone here. Q1 reports are in the shared drive.', D4, 1001),
    makeChatMsg(3003, 'carol@cruvzmail.com', 'Carol', 'Williams', 'Reminder: Team sync is at 3 PM today.', D3, 1001),
    makeChatMsg(3004, 'alice@cruvzmail.com', 'Alice', 'Johnson', "I've shared the brand guidelines in DriveX — check the Marketing Assets folder.", D3, 1001),
    makeChatMsg(3005, 'bob@cruvzmail.com', 'Bob', 'Smith', 'Thanks Alice! The new onboarding deck looks great.', D2, 1001),
    makeChatMsg(3006, 'carol@cruvzmail.com', 'Carol', 'Williams', 'Road-map review is scheduled for Friday. Calendar invite sent.', D1, 1001),
  ],
  1002: [
    makeChatMsg(3010, 'alice@cruvzmail.com', 'Alice', 'Johnson', '📢 All hands meeting this Friday at 10 AM. Conference Room B or join remotely.', D3, 1002),
    makeChatMsg(3011, 'bob@cruvzmail.com', 'Bob', 'Smith', '🎉 Q1 targets exceeded! Full report coming in the drive shortly.', D1, 1002),
  ],
  1003: [
    makeChatMsg(3020, 'carol@cruvzmail.com', 'Carol', 'Williams', 'API latency is down to 98 ms p99 after the last deploy. 🚀', D4, 1003),
    makeChatMsg(3021, 'bob@cruvzmail.com', 'Bob', 'Smith', "Excellent! Let's keep an eye on memory usage over the weekend.", D4, 1003),
    makeChatMsg(3022, 'alice@cruvzmail.com', 'Alice', 'Johnson', 'New CI pipeline is live — builds now finish in under 3 minutes.', D2, 1003),
  ],
};

export const DEMO_DM_MESSAGES: Record<number, ReturnType<typeof makeChatMsg>[]> = {
  2001: [
    makeChatMsg(4001, 'alice@cruvzmail.com', 'Alice', 'Johnson', 'Hey! Did you get a chance to review the proposal?', D3, undefined, 2001),
    makeChatMsg(4002, DEMO_EMAIL, 'Demo', 'User', 'Yes, looks good to me. A few minor tweaks on page 4.', D3, undefined, 2001),
    makeChatMsg(4003, 'alice@cruvzmail.com', 'Alice', 'Johnson', 'Sounds good, see you then!', D2, undefined, 2001),
  ],
  2002: [
    makeChatMsg(4010, 'bob@cruvzmail.com', 'Bob', 'Smith', 'Can you send the Q1 numbers when you get a chance?', D3, undefined, 2002),
    makeChatMsg(4011, 'bob@cruvzmail.com', 'Bob', 'Smith', "I'll review the report tonight.", D3, undefined, 2002),
  ],
  2003: [
    makeChatMsg(4020, 'carol@cruvzmail.com', 'Carol', 'Williams', 'The design mockup is ready for your review.', D1, undefined, 2003),
    makeChatMsg(4021, DEMO_EMAIL, 'Demo', 'User', 'Approved ✅', D1, undefined, 2003),
  ],
};

export const DEMO_ONLINE_USERS = [
  { id: 'u-alice', email: 'alice@cruvzmail.com', firstName: 'Alice', lastName: 'Johnson', status: 'available' as const, lastSeen: D1, domain: 'cruvzmail.com' },
  { id: 'u-bob', email: 'bob@cruvzmail.com', firstName: 'Bob', lastName: 'Smith', status: 'busy' as const, lastSeen: D2, domain: 'cruvzmail.com' },
  { id: 'u-carol', email: 'carol@cruvzmail.com', firstName: 'Carol', lastName: 'Williams', status: 'away' as const, lastSeen: D3, domain: 'cruvzmail.com' },
];

// ─────────── DriveX ───────────

export const DEMO_DRIVE_ITEMS = [
  {
    id: 'df-001', name: 'Q1 Reports', type: 'folder' as const, mimeType: 'folder', fileSize: 0,
    ownerId: 'u-demo', ownerEmail: DEMO_EMAIL, parentId: null,
    sharedWith: [], isShareLinkEnabled: false, isStarred: false, isDeleted: false,
    createdAt: D4, updatedAt: D3,
  },
  {
    id: 'df-002', name: 'Marketing Assets', type: 'folder' as const, mimeType: 'folder', fileSize: 0,
    ownerId: 'u-demo', ownerEmail: DEMO_EMAIL, parentId: null,
    sharedWith: [], isShareLinkEnabled: false, isStarred: true, isDeleted: false,
    createdAt: D4, updatedAt: D2,
  },
  {
    id: 'df-003', name: 'Company Policies', type: 'folder' as const, mimeType: 'folder', fileSize: 0,
    ownerId: 'u-demo', ownerEmail: DEMO_EMAIL, parentId: null,
    sharedWith: [], isShareLinkEnabled: false, isStarred: false, isDeleted: false,
    createdAt: D4, updatedAt: D4,
  },
  {
    id: 'df-004', name: 'Q1 Financial Report.xlsx', type: 'file' as const,
    mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', fileSize: 245760,
    ownerId: 'u-demo', ownerEmail: DEMO_EMAIL, parentId: null,
    sharedWith: [{ userId: 'u-alice', email: 'alice@cruvzmail.com', permission: 'view', sharedAt: D3 }],
    isShareLinkEnabled: false, isStarred: true, isDeleted: false,
    createdAt: D3, updatedAt: D2,
  },
  {
    id: 'df-005', name: 'Brand Guidelines.pdf', type: 'file' as const, mimeType: 'application/pdf', fileSize: 3145728,
    ownerId: 'u-demo', ownerEmail: DEMO_EMAIL, parentId: null,
    sharedWith: [], isShareLinkEnabled: true, shareLinkPermission: 'view', isStarred: false, isDeleted: false,
    createdAt: D4, updatedAt: D4,
  },
  {
    id: 'df-006', name: 'Employee Handbook.pdf', type: 'file' as const, mimeType: 'application/pdf', fileSize: 1048576,
    ownerId: 'u-demo', ownerEmail: DEMO_EMAIL, parentId: null,
    sharedWith: [], isShareLinkEnabled: false, isStarred: false, isDeleted: false,
    createdAt: D4, updatedAt: D4,
  },
  {
    id: 'df-007', name: 'Project Roadmap.docx', type: 'file' as const,
    mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', fileSize: 102400,
    ownerId: 'u-demo', ownerEmail: DEMO_EMAIL, parentId: null,
    sharedWith: [{ userId: 'u-bob', email: 'bob@cruvzmail.com', permission: 'edit', sharedAt: D2 }],
    isShareLinkEnabled: false, isStarred: true, isDeleted: false,
    createdAt: D2, updatedAt: D1,
  },
];

export const DEMO_DRIVE_STORAGE = {
  totalFiles: 4,
  totalFolders: 3,
  totalSize: 4542464,
  quotaBytes: 10737418240,
  quotaUsedBytes: 4542464,
};

// ─────────── Docs / Sheets ───────────

export const DEMO_DOCUMENTS = [
  {
    id: 'doc-001', title: 'Q1 Business Review', type: 'document', fileKey: 'demo/doc-001.docx', fileSize: 184320,
    ownerId: 'u-demo', ownerEmail: DEMO_EMAIL,
    sharedWith: [{ userId: 'u-alice', email: 'alice@cruvzmail.com', permission: 'view', sharedAt: D3 }],
    isShareLinkEnabled: false, isDeleted: false, createdAt: D4, updatedAt: D2,
  },
  {
    id: 'doc-002', title: 'Marketing Plan 2026', type: 'document', fileKey: 'demo/doc-002.docx', fileSize: 92160,
    ownerId: 'u-demo', ownerEmail: DEMO_EMAIL, sharedWith: [], isShareLinkEnabled: false,
    isDeleted: false, createdAt: D3, updatedAt: D3,
  },
  {
    id: 'doc-003', title: 'Onboarding Guide', type: 'document', fileKey: 'demo/doc-003.docx', fileSize: 65536,
    ownerId: 'u-alice', ownerEmail: 'alice@cruvzmail.com',
    sharedWith: [{ userId: 'u-demo', email: DEMO_EMAIL, permission: 'view', sharedAt: D2 }],
    isShareLinkEnabled: true, shareLinkPermission: 'view', isDeleted: false, createdAt: D4, updatedAt: D1,
  },
];

export const DEMO_SPREADSHEETS = [
  {
    id: 'sht-001', title: 'Budget Tracker 2026', type: 'spreadsheet', fileKey: 'demo/sht-001.xlsx', fileSize: 245760,
    ownerId: 'u-demo', ownerEmail: DEMO_EMAIL,
    sharedWith: [{ userId: 'u-bob', email: 'bob@cruvzmail.com', permission: 'edit', sharedAt: D3 }],
    isShareLinkEnabled: false, isDeleted: false, createdAt: D4, updatedAt: D2,
  },
  {
    id: 'sht-002', title: 'KPI Dashboard', type: 'spreadsheet', fileKey: 'demo/sht-002.xlsx', fileSize: 131072,
    ownerId: 'u-demo', ownerEmail: DEMO_EMAIL, sharedWith: [], isShareLinkEnabled: false,
    isDeleted: false, createdAt: D3, updatedAt: D3,
  },
  {
    id: 'sht-003', title: 'Q1 Sales Pipeline', type: 'spreadsheet', fileKey: 'demo/sht-003.xlsx', fileSize: 98304,
    ownerId: 'u-carol', ownerEmail: 'carol@cruvzmail.com',
    sharedWith: [{ userId: 'u-demo', email: DEMO_EMAIL, permission: 'view', sharedAt: D1 }],
    isShareLinkEnabled: false, isDeleted: false, createdAt: D2, updatedAt: D1,
  },
];

// ─────────── Inbox / Mail ───────────

// Precise recent timestamps for realism
const DT1  = '2026-05-26T09:12:00.000Z'; // today morning
const DT2  = '2026-05-26T07:45:00.000Z'; // today early
const DT3  = '2026-05-25T16:30:00.000Z'; // yesterday afternoon
const DT4  = '2026-05-25T11:05:00.000Z'; // yesterday morning
const DT5  = '2026-05-24T14:20:00.000Z'; // 2 days ago
const DT6  = '2026-05-23T09:50:00.000Z'; // 3 days ago
const DT7  = '2026-05-22T17:15:00.000Z'; // 4 days ago
const DT8  = '2026-05-21T13:40:00.000Z'; // 5 days ago
const DT9  = '2026-05-20T10:00:00.000Z'; // 6 days ago
const DT10 = '2026-05-19T08:30:00.000Z'; // 7 days ago

export const DEMO_EMAILS = [
  // ── 12 inbox emails (4 unread, 1 flagged, 1 starred) ──
  {
    id: 'em-001', messageId: '<demo-001@cruvzmail.com>',
    subject: 'Q1 Performance Review – Action Required',
    from: { name: 'Alice Johnson', address: 'alice@cruvzmail.com' },
    to: [{ name: 'Demo User', address: DEMO_EMAIL }],
    body: 'Hi Demo,\n\nPlease review the Q1 performance metrics attached and add your comments by Friday EOD. The board presentation is next Monday.\n\nThanks,\nAlice',
    htmlBody: '<p>Hi Demo,</p><p>Please review the Q1 performance metrics attached and add your comments by Friday EOD. The board presentation is next Monday.</p><p>Thanks,<br/>Alice</p>',
    date: DT1, receivedAt: DT1, isRead: false, isStarred: true, isFlagged: false, isImportant: true,
    isDraft: false, isPinned: false, isSnoozed: false, labels: ['Work'], folder: 'inbox', attachments: [],
  },
  {
    id: 'em-002', messageId: '<demo-002@cruvzmail.com>',
    subject: 'Team Sync – Monday 10 AM Agenda',
    from: { name: 'Bob Smith', address: 'bob@cruvzmail.com' },
    to: [{ name: 'Demo User', address: DEMO_EMAIL }],
    body: 'Hi team,\n\nSharing the agenda for Monday\'s sync:\n1. Q1 wrap-up\n2. Q2 roadmap priorities\n3. New hire introductions\n\nSee you there!\nBob',
    htmlBody: '<p>Hi team,</p><ol><li>Q1 wrap-up</li><li>Q2 roadmap priorities</li><li>New hire introductions</li></ol><p>See you there!<br/>Bob</p>',
    date: DT2, receivedAt: DT2, isRead: true, isStarred: false, isFlagged: false, isImportant: false,
    isDraft: false, isPinned: false, isSnoozed: false, labels: [], folder: 'inbox', attachments: [],
  },
  {
    id: 'em-003', messageId: '<demo-003@cruvzmail.com>',
    subject: 'Invoice #INV-2026-041 from Acme Corp',
    from: { name: 'Acme Corp Billing', address: 'billing@acmecorp.com' },
    to: [{ name: 'Demo User', address: DEMO_EMAIL }],
    body: 'Please find attached invoice #INV-2026-041 for services rendered in April 2026. Amount due: $3,450.00. Payment due by 15 June 2026.',
    htmlBody: '<p>Please find attached invoice <strong>#INV-2026-041</strong> for services rendered in April 2026.</p><p><strong>Amount due:</strong> $3,450.00<br/><strong>Due date:</strong> 15 June 2026</p>',
    date: DT3, receivedAt: DT3, isRead: false, isStarred: false, isFlagged: false, isImportant: true,
    isDraft: false, isPinned: false, isSnoozed: false, labels: ['Work'], folder: 'inbox', attachments: [],
  },
  {
    id: 'em-004', messageId: '<demo-004@cruvzmail.com>',
    subject: 'Brand Guidelines v2.3 – Please Review',
    from: { name: 'Carol Williams', address: 'carol@cruvzmail.com' },
    to: [{ name: 'Demo User', address: DEMO_EMAIL }],
    body: 'Hi Demo,\n\nThe updated brand guidelines (v2.3) are live in DriveX under Marketing Assets. Key changes include the new primary palette and updated logo clearance zones. Please review before the campaign launch on June 1st.\n\nCarol',
    htmlBody: '<p>Hi Demo,</p><p>The updated brand guidelines (v2.3) are live in DriveX under <em>Marketing Assets</em>. Key changes include the new primary palette and updated logo clearance zones. Please review before the campaign launch on June 1st.</p><p>Carol</p>',
    date: DT4, receivedAt: DT4, isRead: true, isStarred: false, isFlagged: false, isImportant: false,
    isDraft: false, isPinned: false, isSnoozed: false, labels: [], folder: 'inbox', attachments: [],
  },
  {
    id: 'em-005', messageId: '<demo-005@cruvzmail.com>',
    subject: '⚠ Security Alert: New Device Login Detected',
    from: { name: 'Cruvzmail Security', address: 'security@cruvzmail.com' },
    to: [{ name: 'Demo User', address: DEMO_EMAIL }],
    body: 'A new login to your Cruvzmail account was detected from Chrome on Windows (IP: 103.21.45.x). If this was you, no action is needed. If not, please change your password immediately.',
    htmlBody: '<p>A new login to your Cruvzmail account was detected from <strong>Chrome on Windows</strong> (IP: 103.21.45.x).</p><p>If this was you, no action is needed. If not, please <a href="#">change your password</a> immediately.</p>',
    date: DT5, receivedAt: DT5, isRead: false, isStarred: false, isFlagged: true, isImportant: true,
    isDraft: false, isPinned: false, isSnoozed: false, labels: [], folder: 'inbox', attachments: [],
  },
  {
    id: 'em-006', messageId: '<demo-006@cruvzmail.com>',
    subject: 'All Hands Meeting – May 30th 10 AM',
    from: { name: 'Alice Johnson', address: 'alice@cruvzmail.com' },
    to: [{ name: 'Demo User', address: DEMO_EMAIL }],
    body: 'Hi everyone,\n\nAll-hands meeting is scheduled for May 30th at 10 AM in Main Hall (and remote via the join link). Full agenda will be shared on Friday.\n\nAlice',
    htmlBody: '<p>Hi everyone,</p><p>All-hands meeting is scheduled for <strong>May 30th at 10 AM</strong> in Main Hall (and remote via the join link). Full agenda will be shared on Friday.</p><p>Alice</p>',
    date: DT5, receivedAt: DT5, isRead: true, isStarred: false, isFlagged: false, isImportant: false,
    isDraft: false, isPinned: false, isSnoozed: false, labels: [], folder: 'inbox', attachments: [],
  },
  {
    id: 'em-007', messageId: '<demo-007@cruvzmail.com>',
    subject: 'Storage Quota Increase Approved',
    from: { name: 'Bob Smith', address: 'bob@cruvzmail.com' },
    to: [{ name: 'Demo User', address: DEMO_EMAIL }],
    body: 'Hi Demo,\n\nYour storage quota increase request has been approved. New quota: 50 GB effective immediately. You can verify this in Settings > Storage.\n\nBob',
    htmlBody: '<p>Hi Demo,</p><p>Your storage quota increase request has been approved. <strong>New quota: 50 GB</strong> effective immediately. You can verify this in Settings &gt; Storage.</p><p>Bob</p>',
    date: DT6, receivedAt: DT6, isRead: true, isStarred: false, isFlagged: false, isImportant: false,
    isDraft: false, isPinned: false, isSnoozed: false, labels: [], folder: 'inbox', attachments: [],
  },
  {
    id: 'em-008', messageId: '<demo-008@cruvzmail.com>',
    subject: 'HR Update: Revised Leave Policy Effective June 1',
    from: { name: 'HR Department', address: 'hr@cruvzmail.com' },
    to: [{ name: 'Demo User', address: DEMO_EMAIL }],
    body: 'Dear Team,\n\nPlease be informed that our leave policy has been revised and will take effect from June 1, 2026. Key changes:\n• Annual leave increased to 24 days\n• Sick leave carry-over up to 5 days\n• New paternity leave: 10 days\n\nFull policy document is in the Company Policies folder on DriveX.',
    htmlBody: '<p>Dear Team,</p><p>Our leave policy has been revised and will take effect from <strong>June 1, 2026</strong>. Key changes:</p><ul><li>Annual leave increased to 24 days</li><li>Sick leave carry-over up to 5 days</li><li>New paternity leave: 10 days</li></ul><p>Full policy is in DriveX &gt; Company Policies.</p>',
    date: DT6, receivedAt: DT6, isRead: true, isStarred: false, isFlagged: false, isImportant: false,
    isDraft: false, isPinned: false, isSnoozed: false, labels: ['Work'], folder: 'inbox', attachments: [],
  },
  {
    id: 'em-009', messageId: '<demo-009@cruvzmail.com>',
    subject: 'Re: Feature Request – Dark Mode Enhancements',
    from: { name: 'David Chen', address: 'david@acmecorp.com' },
    to: [{ name: 'Demo User', address: DEMO_EMAIL }],
    body: 'Thanks for the update! The new dark mode looks great. Our users are really happy with the contrast improvements. Looking forward to the next release.\n\nDavid',
    htmlBody: '<p>Thanks for the update! The new dark mode looks great. Our users are really happy with the contrast improvements. Looking forward to the next release.</p><p>David</p>',
    date: DT7, receivedAt: DT7, isRead: true, isStarred: false, isFlagged: false, isImportant: false,
    isDraft: false, isPinned: false, isSnoozed: false, labels: [], folder: 'inbox', attachments: [],
  },
  {
    id: 'em-010', messageId: '<demo-010@cruvzmail.com>',
    subject: 'Contract Renewal Reminder – Expires June 30',
    from: { name: 'Legal Department', address: 'legal@cruvzmail.com' },
    to: [{ name: 'Demo User', address: DEMO_EMAIL }],
    body: 'Hi Demo,\n\nThis is a reminder that the vendor contract with Acme Corp expires on June 30, 2026. Please review the renewal terms and confirm by June 10 so we can process in time.\n\nLegal Team',
    htmlBody: '<p>Hi Demo,</p><p>This is a reminder that the vendor contract with <strong>Acme Corp</strong> expires on <strong>June 30, 2026</strong>. Please review the renewal terms and confirm by June 10.</p><p>Legal Team</p>',
    date: DT8, receivedAt: DT8, isRead: false, isStarred: false, isFlagged: false, isImportant: true,
    isDraft: false, isPinned: false, isSnoozed: false, labels: ['Work'], folder: 'inbox', attachments: [],
  },
  {
    id: 'em-011', messageId: '<demo-011@cruvzmail.com>',
    subject: 'Cruvzmail Monthly Newsletter – May 2026',
    from: { name: 'Cruvzmail Team', address: 'newsletter@cruvzmail.com' },
    to: [{ name: 'Demo User', address: DEMO_EMAIL }],
    body: "This month's highlights: New DriveX collaboration features, improved spam filtering, mobile app v3.2 release, and upcoming webinar on May 29. Read more at cruvzmail.com/news.",
    htmlBody: "<p>This month's highlights:</p><ul><li>New DriveX collaboration features</li><li>Improved spam filtering</li><li>Mobile app v3.2 release</li><li>Upcoming webinar – May 29</li></ul><p>Read more at <a href='#'>cruvzmail.com/news</a></p>",
    date: DT9, receivedAt: DT9, isRead: true, isStarred: false, isFlagged: false, isImportant: false,
    isDraft: false, isPinned: false, isSnoozed: false, labels: [], folder: 'inbox', attachments: [],
  },
  {
    id: 'em-012', messageId: '<demo-012@cruvzmail.com>',
    subject: 'Welcome to Cruvzmail Enterprise!',
    from: { name: 'Cruvzmail Onboarding', address: 'onboarding@cruvzmail.com' },
    to: [{ name: 'Demo User', address: DEMO_EMAIL }],
    body: "Welcome aboard! Your Cruvzmail Enterprise account is ready. You have access to Email, DriveX, Team Chat, Helpdesk, Calendar, and more. Get started at mail.cruvzmail.com.\n\nThe Cruvzmail Team",
    htmlBody: "<p>Welcome aboard! 🎉</p><p>Your Cruvzmail Enterprise account is ready. You have access to:</p><ul><li>Email &amp; Calendar</li><li>DriveX (cloud storage)</li><li>Team Chat</li><li>Helpdesk</li><li>Documents &amp; Sheets</li></ul><p>Get started at <a href='#'>mail.cruvzmail.com</a></p>",
    date: DT10, receivedAt: DT10, isRead: true, isStarred: false, isFlagged: false, isImportant: false,
    isDraft: false, isPinned: false, isSnoozed: false, labels: [], folder: 'inbox', attachments: [],
  },
];

export const DEMO_SENT_EMAILS = [
  {
    id: 'em-101', messageId: '<demo-101@cruvzmail.com>',
    subject: 'Re: Q1 Performance Review – Action Required',
    from: { name: 'Demo User', address: DEMO_EMAIL },
    to: [{ name: 'Alice Johnson', address: 'alice@cruvzmail.com' }],
    body: 'Hi Alice,\n\nThanks for sending this over. I\'ve reviewed the metrics and added my comments inline. A few areas in Customer Success need clarification — I\'ll follow up in tomorrow\'s sync.\n\nDemo',
    htmlBody: '<p>Hi Alice,</p><p>Thanks for sending this over. I\'ve reviewed the metrics and added comments inline. A few areas in Customer Success need clarification — I\'ll follow up in tomorrow\'s sync.</p><p>Demo</p>',
    date: DT1, receivedAt: DT1, isRead: true, isStarred: false, isFlagged: false, isImportant: false,
    isDraft: false, isPinned: false, isSnoozed: false, labels: [], folder: 'sent', attachments: [],
  },
  {
    id: 'em-102', messageId: '<demo-102@cruvzmail.com>',
    subject: 'Re: Invoice #INV-2026-041',
    from: { name: 'Demo User', address: DEMO_EMAIL },
    to: [{ name: 'Acme Corp Billing', address: 'billing@acmecorp.com' }],
    body: 'Hi,\n\nThank you for the invoice. I\'ve forwarded it to our Finance team for processing. Payment will be made before the due date.\n\nBest regards,\nDemo',
    htmlBody: '<p>Hi,</p><p>Thank you for the invoice. I\'ve forwarded it to our Finance team for processing. Payment will be made before the due date.</p><p>Best regards,<br/>Demo</p>',
    date: DT3, receivedAt: DT3, isRead: true, isStarred: false, isFlagged: false, isImportant: false,
    isDraft: false, isPinned: false, isSnoozed: false, labels: [], folder: 'sent', attachments: [],
  },
  {
    id: 'em-103', messageId: '<demo-103@cruvzmail.com>',
    subject: 'Storage Quota Increase Request',
    from: { name: 'Demo User', address: DEMO_EMAIL },
    to: [{ name: 'Bob Smith', address: 'bob@cruvzmail.com' }],
    body: 'Hi Bob,\n\nOur team storage is nearly full (at 47 GB out of 50 GB). Could you process a quota increase to 100 GB? We have several large project files to store in the coming weeks.\n\nThanks,\nDemo',
    htmlBody: '<p>Hi Bob,</p><p>Our team storage is nearly full (47 GB / 50 GB). Could you process a quota increase to 100 GB? We have several large project files incoming.</p><p>Thanks,<br/>Demo</p>',
    date: DT6, receivedAt: DT6, isRead: true, isStarred: false, isFlagged: false, isImportant: false,
    isDraft: false, isPinned: false, isSnoozed: false, labels: [], folder: 'sent', attachments: [],
  },
  {
    id: 'em-104', messageId: '<demo-104@cruvzmail.com>',
    subject: 'Feature Request: Dark Mode Enhancements',
    from: { name: 'Demo User', address: DEMO_EMAIL },
    to: [{ name: 'Carol Williams', address: 'carol@cruvzmail.com' }],
    body: 'Hi Carol,\n\nOur client Acme Corp has requested better contrast in dark mode — specifically in the email list and compose window. Can we add this to the Q2 backlog?\n\nDemo',
    htmlBody: '<p>Hi Carol,</p><p>Our client Acme Corp has requested better contrast in dark mode — specifically in the email list and compose window. Can we add this to the Q2 backlog?</p><p>Demo</p>',
    date: DT7, receivedAt: DT7, isRead: true, isStarred: false, isFlagged: false, isImportant: false,
    isDraft: false, isPinned: false, isSnoozed: false, labels: [], folder: 'sent', attachments: [],
  },
  {
    id: 'em-105', messageId: '<demo-105@cruvzmail.com>',
    subject: 'Q2 Planning – Review Document in DriveX',
    from: { name: 'Demo User', address: DEMO_EMAIL },
    to: [{ name: 'Alice Johnson', address: 'alice@cruvzmail.com' }, { name: 'Bob Smith', address: 'bob@cruvzmail.com' }],
    body: "Hi Alice and Bob,\n\nI've uploaded the Q2 planning document to DriveX (Q1 Reports folder). Please review and leave comments before Friday. Key topics: headcount, budget allocation, and product priorities.\n\nDemo",
    htmlBody: "<p>Hi Alice and Bob,</p><p>I've uploaded the Q2 planning document to DriveX (Q1 Reports folder). Please review and leave comments before Friday. Key topics: headcount, budget allocation, and product priorities.</p><p>Demo</p>",
    date: DT8, receivedAt: DT8, isRead: true, isStarred: false, isFlagged: false, isImportant: false,
    isDraft: false, isPinned: false, isSnoozed: false, labels: [], folder: 'sent', attachments: [],
  },
];

export const DEMO_DRAFT_EMAILS = [
  {
    id: 'em-201', messageId: '<demo-201@cruvzmail.com>',
    subject: 'Q2 Budget Proposal – Draft',
    from: { name: 'Demo User', address: DEMO_EMAIL },
    to: [{ name: 'Alice Johnson', address: 'alice@cruvzmail.com' }],
    body: 'Hi Alice,\n\nPlease find below the preliminary Q2 budget proposal for your review:\n\n[DRAFT - INCOMPLETE]\n\n- Engineering: $120,000\n- Marketing: $85,000\n- Operations: ',
    htmlBody: '<p>Hi Alice,</p><p>Please find below the preliminary Q2 budget proposal:</p><p><em>[DRAFT – INCOMPLETE]</em></p><ul><li>Engineering: $120,000</li><li>Marketing: $85,000</li><li>Operations: </li></ul>',
    date: DT2, receivedAt: DT2, isRead: true, isStarred: false, isFlagged: false, isImportant: false,
    isDraft: true, isPinned: false, isSnoozed: false, labels: [], folder: 'drafts', attachments: [],
  },
  {
    id: 'em-202', messageId: '<demo-202@cruvzmail.com>',
    subject: 'Re: Contract Renewal Reminder',
    from: { name: 'Demo User', address: DEMO_EMAIL },
    to: [{ name: 'Legal Department', address: 'legal@cruvzmail.com' }],
    body: 'Hi Legal Team,\n\nThank you for the reminder. I\'ve reviewed the contract terms and have a few questions:\n\n1. Can the SLA clause be updated to reflect the new response time?\n2. ',
    htmlBody: '<p>Hi Legal Team,</p><p>Thank you for the reminder. I\'ve reviewed the contract terms and have a few questions:</p><ol><li>Can the SLA clause be updated to reflect the new response time?</li><li></li></ol>',
    date: DT4, receivedAt: DT4, isRead: true, isStarred: false, isFlagged: false, isImportant: false,
    isDraft: true, isPinned: false, isSnoozed: false, labels: [], folder: 'drafts', attachments: [],
  },
  {
    id: 'em-203', messageId: '<demo-203@cruvzmail.com>',
    subject: 'Team Outing – June 14th',
    from: { name: 'Demo User', address: DEMO_EMAIL },
    to: [{ name: 'Alice Johnson', address: 'alice@cruvzmail.com' }, { name: 'Bob Smith', address: 'bob@cruvzmail.com' }, { name: 'Carol Williams', address: 'carol@cruvzmail.com' }],
    body: 'Hi team,\n\nI\'m planning a team outing for June 14th. A few options:\n- Bowling at Lakeview Lanes\n- Escape room at MindBenders\n- Rooftop dinner at The Heights\n\nPlease vote by replying with your preference. More details to follow once confirmed.',
    htmlBody: '<p>Hi team,</p><p>I\'m planning a team outing for <strong>June 14th</strong>. Options:</p><ul><li>Bowling at Lakeview Lanes</li><li>Escape room at MindBenders</li><li>Rooftop dinner at The Heights</li></ul><p>Please reply with your preference.</p>',
    date: DT5, receivedAt: DT5, isRead: true, isStarred: false, isFlagged: false, isImportant: false,
    isDraft: true, isPinned: false, isSnoozed: false, labels: [], folder: 'drafts', attachments: [],
  },
];

export const DEMO_SPAM_EMAILS = [
  {
    id: 'em-301', messageId: '<spam-301@spammer.net>',
    subject: 'URGENT: Your account has been compromised! Act NOW',
    from: { name: 'Security Alert', address: 'noreply@security-alert-verify.net' },
    to: [{ name: 'Demo User', address: DEMO_EMAIL }],
    body: 'WARNING! Your account has been accessed by an unknown device. Click the link below IMMEDIATELY to secure your account and verify your identity. Failure to do so within 24 hours will result in account suspension.',
    htmlBody: '<p><strong>WARNING!</strong> Your account has been accessed by an unknown device. <a href="#">Click here IMMEDIATELY</a> to secure your account. Failure to do so within 24 hours will result in account suspension.</p>',
    date: DT3, receivedAt: DT3, isRead: false, isStarred: false, isFlagged: false, isImportant: false,
    isDraft: false, isPinned: false, isSnoozed: false, labels: [], folder: 'spam', attachments: [],
  },
  {
    id: 'em-302', messageId: '<spam-302@lottery-winner.com>',
    subject: '🎉 Congratulations! You\'ve Won $1,000,000 – Claim Now',
    from: { name: 'International Lottery Foundation', address: 'claims@intl-lottery-fund.com' },
    to: [{ name: 'Demo User', address: DEMO_EMAIL }],
    body: "You have been selected as the winner of our $1,000,000 prize draw! To claim your winnings, please reply with your full name, address, and bank account details. This offer expires in 48 hours.",
    htmlBody: "<p>You have been selected as the winner of our <strong>$1,000,000</strong> prize draw! To claim your winnings, please reply with your full name, address, and bank account details. This offer expires in 48 hours.</p>",
    date: DT5, receivedAt: DT5, isRead: false, isStarred: false, isFlagged: false, isImportant: false,
    isDraft: false, isPinned: false, isSnoozed: false, labels: [], folder: 'spam', attachments: [],
  },
  {
    id: 'em-303', messageId: '<spam-303@deals.net>',
    subject: 'Limited Time: 90% OFF Enterprise Software – Today Only!',
    from: { name: 'SoftDeals Pro', address: 'promo@softdeals-promo.biz' },
    to: [{ name: 'Demo User', address: DEMO_EMAIL }],
    body: 'Get Microsoft Office, Adobe Creative Cloud, and 200+ premium tools at 90% off! This exclusive deal expires midnight tonight. Click to claim your discount code. No credit card required.',
    htmlBody: '<p>Get Microsoft Office, Adobe Creative Cloud, and 200+ premium tools at <strong>90% off</strong>! This exclusive deal expires midnight tonight. <a href="#">Click to claim your discount code</a>. No credit card required.</p>',
    date: DT7, receivedAt: DT7, isRead: false, isStarred: false, isFlagged: false, isImportant: false,
    isDraft: false, isPinned: false, isSnoozed: false, labels: [], folder: 'spam', attachments: [],
  },
  {
    id: 'em-304', messageId: '<spam-304@upgrade.net>',
    subject: 'Your Subscription is EXPIRING – Renew or Lose Access',
    from: { name: 'Account Services', address: 'service@account-upgrade-center.net' },
    to: [{ name: 'Demo User', address: DEMO_EMAIL }],
    body: "Your subscription is expiring in 2 days. To continue uninterrupted access, please click the link below to renew now at a special discounted rate. Ignoring this email will result in immediate service termination.",
    htmlBody: "<p>Your subscription is expiring in <strong>2 days</strong>. To continue uninterrupted access, please <a href='#'>renew now</a> at a special discounted rate. Ignoring this email will result in immediate service termination.</p>",
    date: DT9, receivedAt: DT9, isRead: false, isStarred: false, isFlagged: false, isImportant: false,
    isDraft: false, isPinned: false, isSnoozed: false, labels: [], folder: 'spam', attachments: [],
  },
];

export const DEMO_TRASH_EMAILS = [
  {
    id: 'em-401', messageId: '<demo-401@cruvzmail.com>',
    subject: 'April Team Meeting Notes',
    from: { name: 'Bob Smith', address: 'bob@cruvzmail.com' },
    to: [{ name: 'Demo User', address: DEMO_EMAIL }],
    body: 'Hi team, please find the April team meeting notes below. These have been superseded by the updated May notes — feel free to archive or delete.',
    htmlBody: '<p>Hi team, please find the April team meeting notes below. These have been superseded by the updated May notes — feel free to archive or delete.</p>',
    date: DT8, receivedAt: DT8, isRead: true, isStarred: false, isFlagged: false, isImportant: false,
    isDraft: false, isPinned: false, isSnoozed: false, labels: [], folder: 'trash', attachments: [],
  },
  {
    id: 'em-402', messageId: '<demo-402@cruvzmail.com>',
    subject: 'Duplicate: Team Sync Reminder (Friday)',
    from: { name: 'Bob Smith', address: 'bob@cruvzmail.com' },
    to: [{ name: 'Demo User', address: DEMO_EMAIL }],
    body: 'Duplicate message — please ignore. The correct invite has already been sent.',
    htmlBody: '<p>Duplicate message — please ignore. The correct invite has already been sent.</p>',
    date: DT9, receivedAt: DT9, isRead: true, isStarred: false, isFlagged: false, isImportant: false,
    isDraft: false, isPinned: false, isSnoozed: false, labels: [], folder: 'trash', attachments: [],
  },
  {
    id: 'em-403', messageId: '<demo-403@cruvzmail.com>',
    subject: 'Test Email – Please Ignore',
    from: { name: 'Demo User', address: DEMO_EMAIL },
    to: [{ name: 'Demo User', address: DEMO_EMAIL }],
    body: 'This is a test email sent during account setup. It can be safely deleted.',
    htmlBody: '<p>This is a test email sent during account setup. It can be safely deleted.</p>',
    date: DT10, receivedAt: DT10, isRead: true, isStarred: false, isFlagged: false, isImportant: false,
    isDraft: false, isPinned: false, isSnoozed: false, labels: [], folder: 'trash', attachments: [],
  },
];

// ─────────── Streams ───────────

export const DEMO_STREAMS = [
  { id: 'str-001', name: 'Company Updates', description: 'Official company news and updates', type: 'public' as const, memberCount: 42, unreadCount: 2, isMuted: false, isOwner: false },
  { id: 'str-002', name: 'Engineering', description: 'Engineering team discussions', type: 'team' as const, memberCount: 12, unreadCount: 0, isMuted: false, isOwner: false },
  { id: 'str-003', name: 'Product Roadmap', description: 'Product planning and roadmap discussions', type: 'team' as const, memberCount: 8, unreadCount: 1, isMuted: false, isOwner: false },
];

export const DEMO_STREAM_POSTS = [
  {
    id: 'sp-001',
    author: { name: 'Alice Johnson', email: 'alice@cruvzmail.com' },
    content: "Q1 targets exceeded! Full report is now in DriveX. Great work everyone! \uD83C\uDF89",
    timestamp: new Date(D1),
    likes: ['bob@cruvzmail.com', 'carol@cruvzmail.com'],
    comments: [
      { id: 'sc-001', author: { name: 'Bob Smith', email: 'bob@cruvzmail.com' }, content: 'Fantastic result!', timestamp: new Date(D1), likes: [] },
    ],
    isBookmarked: false, streamId: 'str-001', streamName: 'Company Updates',
  },
  {
    id: 'sp-002',
    author: { name: 'Carol Williams', email: 'carol@cruvzmail.com' },
    content: "New CI pipeline is live. Builds now finish in under 3 minutes on average. \uD83D\uDE80",
    timestamp: new Date(D2),
    likes: ['alice@cruvzmail.com'],
    comments: [],
    isBookmarked: false, streamId: 'str-002', streamName: 'Engineering',
  },
  {
    id: 'sp-003',
    author: { name: 'Bob Smith', email: 'bob@cruvzmail.com' },
    content: 'Road-map planning session is scheduled for Friday 2 PM. Please review the Q2 proposals in DriveX before the meeting.',
    timestamp: new Date(D3),
    likes: [],
    comments: [],
    isBookmarked: true, streamId: 'str-003', streamName: 'Product Roadmap',
  },
];

// ─────────── Contacts ───────────

export const DEMO_CONTACTS = [
  {
    id: 'ct-001', firstName: 'Alice', lastName: 'Johnson',
    emails: [{ type: 'work', email: 'alice@cruvzmail.com', isPrimary: true }],
    phones: [{ type: 'work', number: '+1 555-0101', isPrimary: true }],
    company: 'Cruvzmail', jobTitle: 'Operations Manager', department: 'Operations',
    addresses: [], birthday: '', notes: 'Key contact for operations queries.',
    tags: ['Work', 'VIP'], groups: [], socialProfiles: [], starred: true,
    createdAt: new Date(D4), updatedAt: new Date(D3), activities: [],
  },
  {
    id: 'ct-002', firstName: 'Bob', lastName: 'Smith',
    emails: [{ type: 'work', email: 'bob@cruvzmail.com', isPrimary: true }],
    phones: [{ type: 'mobile', number: '+1 555-0102', isPrimary: true }],
    company: 'Cruvzmail', jobTitle: 'Finance Lead', department: 'Finance',
    addresses: [], birthday: '', notes: '',
    tags: ['Work'], groups: [], socialProfiles: [], starred: false,
    createdAt: new Date(D4), updatedAt: new Date(D4), activities: [],
  },
  {
    id: 'ct-003', firstName: 'Carol', lastName: 'Williams',
    emails: [{ type: 'work', email: 'carol@cruvzmail.com', isPrimary: true }],
    phones: [{ type: 'work', number: '+1 555-0103', isPrimary: true }],
    company: 'Cruvzmail', jobTitle: 'Lead Engineer', department: 'Engineering',
    addresses: [], birthday: '', notes: 'Handles CI/CD and infrastructure.',
    tags: ['Work', 'Partner'], groups: [], socialProfiles: [], starred: true,
    createdAt: new Date(D4), updatedAt: new Date(D2), activities: [],
  },
  {
    id: 'ct-004', firstName: 'David', lastName: 'Chen',
    emails: [{ type: 'work', email: 'david@acmecorp.com', isPrimary: true }],
    phones: [{ type: 'work', number: '+1 555-0104', isPrimary: true }],
    company: 'Acme Corp', jobTitle: 'Procurement Manager', department: 'Procurement',
    addresses: [], birthday: '', notes: '',
    tags: ['Client'], groups: [], socialProfiles: [], starred: false,
    createdAt: new Date(D3), updatedAt: new Date(D3), activities: [],
  },
];

// ─────────── Calendar ───────────

export const DEMO_CALENDARS = [
  { id: 'cal-001', name: 'My Calendar', color: '#1976d2', isVisible: true, isDefault: true },
  { id: 'cal-002', name: 'Company Events', color: '#388e3c', isVisible: true, isDefault: false },
];

export const DEMO_CALENDAR_EVENTS = [
  {
    id: 'ev-001', title: 'Team Sync', date: new Date('2026-05-08T15:00:00'), startTime: '15:00', endTime: '16:00',
    allDay: false, description: 'Weekly team synchronization', location: 'Conference Room B',
    color: '#1976d2', calendarId: 'cal-001',
    attendees: [
      { email: 'alice@cruvzmail.com', name: 'Alice Johnson', status: 'accepted' },
      { email: 'bob@cruvzmail.com', name: 'Bob Smith', status: 'accepted' },
    ],
    reminders: [], recurrence: null, videoLink: '',
    createdAt: new Date(D4), updatedAt: new Date(D4),
  },
  {
    id: 'ev-002', title: 'All Hands Meeting', date: new Date('2026-05-09T10:00:00'), startTime: '10:00', endTime: '11:30',
    allDay: false, description: 'Company-wide all hands meeting', location: 'Main Hall',
    color: '#388e3c', calendarId: 'cal-002',
    attendees: [], reminders: [], recurrence: null, videoLink: 'https://meet.cruvzmail.com/allhands',
    createdAt: new Date(D4), updatedAt: new Date(D4),
  },
  {
    id: 'ev-003', title: 'Road-map Planning', date: new Date('2026-05-07T14:00:00'), startTime: '14:00', endTime: '15:30',
    allDay: false, description: 'Q2 road-map planning session', location: 'Conference Room A',
    color: '#1976d2', calendarId: 'cal-001',
    attendees: [{ email: 'carol@cruvzmail.com', name: 'Carol Williams', status: 'accepted' }],
    reminders: [], recurrence: null, videoLink: '',
    createdAt: new Date(D3), updatedAt: new Date(D3),
  },
];

// ─────────── Notes ───────────

export const DEMO_NOTES = [
  {
    id: 'nt-001', title: 'Q1 Review Talking Points',
    content: '<ul><li>Revenue grew 18% YoY</li><li>Customer churn reduced by 4%</li><li>Onboarding time cut from 5 days to 2</li></ul>',
    color: '#fff9c4', pinned: true, starred: true, folderId: 'all', tags: ['meeting', 'planning'],
    checklist: [], isChecklist: false, attachments: [], collaborators: [], isLocked: false,
    archived: false, trashed: false, versions: [], wordCount: 22, characterCount: 130,
    createdAt: new Date(D4), updatedAt: new Date(D3),
  },
  {
    id: 'nt-002', title: 'Onboarding Checklist', content: '',
    color: '#ffffff', pinned: false, starred: false, folderId: 'all', tags: ['planning'],
    checklist: [
      { id: 'c1', text: 'Send welcome email', checked: true },
      { id: 'c2', text: 'Provision accounts', checked: true },
      { id: 'c3', text: 'Schedule orientation', checked: false },
      { id: 'c4', text: 'Assign onboarding buddy', checked: false },
    ],
    isChecklist: true, attachments: [], collaborators: [], isLocked: false,
    archived: false, trashed: false, versions: [], wordCount: 0, characterCount: 0,
    createdAt: new Date(D3), updatedAt: new Date(D2),
  },
  {
    id: 'nt-003', title: 'Engineering Notes',
    content: '<p>API p99 latency: 98 ms post-deploy. Monitor memory over weekend.</p><p>New CI pipeline average: 3 min builds.</p>',
    color: '#e3f2fd', pinned: false, starred: false, folderId: 'all', tags: ['technical', 'work'],
    checklist: [], isChecklist: false, attachments: [], collaborators: [], isLocked: false,
    archived: false, trashed: false, versions: [], wordCount: 18, characterCount: 115,
    createdAt: new Date(D4), updatedAt: new Date(D1),
  },
];

// ─────────── Tasks ───────────

export const DEMO_PROJECTS = [
  { id: 'pr-001', name: 'Q2 Planning', color: '#1976d2', icon: '\uD83D\uDCCB', taskCount: 3 },
  { id: 'pr-002', name: 'Onboarding', color: '#388e3c', icon: '\uD83D\uDC65', taskCount: 2 },
];

export const DEMO_TASKS = [
  {
    id: 'tk-001', title: 'Review Q1 performance report', description: 'Check the metrics from Alice and add comments',
    status: 'in-progress', priority: 'high', dueDate: '2026-05-09',
    projectId: 'pr-001', tags: ['review', 'report'], subtasks: [], assignees: [], attachments: [],
    starred: true, recurrence: null, reminder: null,
    createdAt: new Date(D3), updatedAt: new Date(D3),
  },
  {
    id: 'tk-002', title: 'Prepare Friday all-hands agenda', description: '',
    status: 'not-started', priority: 'medium', dueDate: '2026-05-08',
    projectId: 'pr-001', tags: ['meeting'], subtasks: [], assignees: [], attachments: [],
    starred: false, recurrence: null, reminder: null,
    createdAt: new Date(D3), updatedAt: new Date(D3),
  },
  {
    id: 'tk-003', title: 'Submit storage quota request', description: '',
    status: 'completed', priority: 'low', dueDate: '2026-05-07',
    projectId: 'pr-001', tags: [], subtasks: [], assignees: [], attachments: [],
    starred: false, recurrence: null, reminder: null,
    createdAt: new Date(D4), updatedAt: new Date(D2),
  },
  {
    id: 'tk-004', title: 'Set up new employee accounts', description: '5 new hires starting Monday',
    status: 'completed', priority: 'high', dueDate: '2026-05-06',
    projectId: 'pr-002', tags: ['urgent'], subtasks: [], assignees: [], attachments: [],
    starred: false, recurrence: null, reminder: null,
    createdAt: new Date(D4), updatedAt: new Date(D1),
  },
  {
    id: 'tk-005', title: 'Schedule orientation sessions', description: '',
    status: 'in-progress', priority: 'medium', dueDate: '2026-05-09',
    projectId: 'pr-002', tags: ['planning'], subtasks: [], assignees: [], attachments: [],
    starred: false, recurrence: null, reminder: null,
    createdAt: new Date(D3), updatedAt: new Date(D3),
  },
];

// ─────────── Bookmarks ───────────

export const DEMO_BOOKMARKS = [
  {
    id: 'bm-001', title: 'Q1 Performance Review Email', description: 'Bookmarked for end of quarter review',
    type: 'email' as const, emailId: 'em-001', folder: 'all', tags: ['important'],
    isStarred: true, from: 'alice@cruvzmail.com', subject: 'Q1 Performance Review - Action Required',
    createdAt: new Date(D3),
  },
  {
    id: 'bm-002', title: 'Cruvzmail Documentation', description: 'Official product documentation portal',
    type: 'link' as const, url: 'https://cruvzmail.com/docs', folder: 'all', tags: ['work', 'reference'],
    isStarred: false, createdAt: new Date(D4),
  },
  {
    id: 'bm-003', title: 'Brand Guidelines PDF', description: '',
    type: 'attachment' as const, emailId: 'em-003', folder: 'all', tags: [],
    isStarred: true, from: 'carol@cruvzmail.com', subject: 'Brand Guidelines Updated',
    createdAt: new Date(D4),
  },
];

// ─────────── Helpdesk ───────────

export const DEMO_TICKETS = [
  {
    id: 'tkt-001', ticketNumber: 'TKT-1001', subject: 'Cannot access email on mobile app after update',
    description: 'I am unable to log in to the Cruvzmail mobile app after the recent v3.2 update. The app shows "Invalid session" and immediately logs me out. I have tried re-installing the app but the issue persists.',
    category: 'technical', priority: 'high', status: 'open',
    userId: 'u-demo', organizationId: 1, assignedTo: 'u-alice',
    requesterEmail: DEMO_EMAIL, requesterName: 'Demo User', isExternal: false,
    createdAt: DT3, updatedAt: DT1,
    userEmail: DEMO_EMAIL, userName: 'Demo User',
    assignedEmail: 'alice@cruvzmail.com', assignedName: 'Alice Johnson',
    replyCount: 2,
  },
  {
    id: 'tkt-002', ticketNumber: 'TKT-1002', subject: 'Storage quota increase request – 100 GB',
    description: 'Our team storage is at 47 GB out of 50 GB. We have several large design files and project assets coming in over the next few weeks. Requesting an increase to 100 GB.',
    category: 'billing', priority: 'medium', status: 'in_progress',
    userId: 'u-demo', organizationId: 1, assignedTo: 'u-bob',
    requesterEmail: DEMO_EMAIL, requesterName: 'Demo User', isExternal: false,
    createdAt: DT4, updatedAt: DT2,
    userEmail: DEMO_EMAIL, userName: 'Demo User',
    assignedEmail: 'bob@cruvzmail.com', assignedName: 'Bob Smith',
    replyCount: 3,
  },
  {
    id: 'tkt-003', ticketNumber: 'TKT-1003', subject: 'Provision 5 new user accounts – May onboarding batch',
    description: 'We have 5 new employees starting on June 2. Please create accounts with standard settings: j.doe@cruvzmail.com, k.patel@cruvzmail.com, m.chen@cruvzmail.com, r.garcia@cruvzmail.com, l.johnson@cruvzmail.com.',
    category: 'general', priority: 'medium', status: 'resolved',
    userId: 'u-demo', organizationId: 1, assignedTo: 'u-carol',
    requesterEmail: DEMO_EMAIL, requesterName: 'Demo User', isExternal: false,
    createdAt: DT7, updatedAt: DT4,
    userEmail: DEMO_EMAIL, userName: 'Demo User',
    assignedEmail: 'carol@cruvzmail.com', assignedName: 'Carol Williams',
    replyCount: 4,
  },
  {
    id: 'tkt-004', ticketNumber: 'TKT-1004', subject: 'HTML email signature not rendering in Outlook 2019',
    description: 'My HTML email signature with company logo appears completely unstyled when recipients open it in Microsoft Outlook 2019. The logo is missing and the formatting is broken. Renders fine in Gmail and Apple Mail.',
    category: 'technical', priority: 'low', status: 'open',
    userId: 'u-demo', organizationId: 1, assignedTo: null,
    requesterEmail: DEMO_EMAIL, requesterName: 'Demo User', isExternal: false,
    createdAt: DT5, updatedAt: DT5,
    userEmail: DEMO_EMAIL, userName: 'Demo User',
    assignedEmail: null, assignedName: null,
    replyCount: 0,
  },
  {
    id: 'tkt-005', ticketNumber: 'TKT-1005', subject: 'Bulk email import from previous provider (Zoho)',
    description: 'We migrated from Zoho Mail last month and need to import approximately 8,000 historical emails. We have the .mbox export file ready. Please advise on the import procedure or assist with the migration.',
    category: 'general', priority: 'high', status: 'in_progress',
    userId: 'u-demo', organizationId: 1, assignedTo: 'u-alice',
    requesterEmail: DEMO_EMAIL, requesterName: 'Demo User', isExternal: false,
    createdAt: DT6, updatedAt: DT3,
    userEmail: DEMO_EMAIL, userName: 'Demo User',
    assignedEmail: 'alice@cruvzmail.com', assignedName: 'Alice Johnson',
    replyCount: 5,
  },
  {
    id: 'tkt-006', ticketNumber: 'TKT-1006', subject: 'Team Chat – Messages not loading in Engineering channel',
    description: 'The Engineering private channel in Team Chat is showing a spinner indefinitely. Other channels load fine. This started happening after I was re-added to the channel yesterday.',
    category: 'technical', priority: 'medium', status: 'open',
    userId: 'u-demo', organizationId: 1, assignedTo: 'u-carol',
    requesterEmail: DEMO_EMAIL, requesterName: 'Demo User', isExternal: false,
    createdAt: DT2, updatedAt: DT1,
    userEmail: DEMO_EMAIL, userName: 'Demo User',
    assignedEmail: 'carol@cruvzmail.com', assignedName: 'Carol Williams',
    replyCount: 1,
  },
  {
    id: 'tkt-007', ticketNumber: 'TKT-1007', subject: 'Add custom domain alias: mail@demo-brand.com',
    description: 'We need to add a domain alias so emails sent to mail@demo-brand.com are delivered to our Cruvzmail inbox. DNS records have already been verified. Please configure the alias on our account.',
    category: 'billing', priority: 'medium', status: 'resolved',
    userId: 'u-demo', organizationId: 1, assignedTo: 'u-bob',
    requesterEmail: DEMO_EMAIL, requesterName: 'Demo User', isExternal: false,
    createdAt: DT9, updatedAt: DT6,
    userEmail: DEMO_EMAIL, userName: 'Demo User',
    assignedEmail: 'bob@cruvzmail.com', assignedName: 'Bob Smith',
    replyCount: 3,
  },
  {
    id: 'tkt-008', ticketNumber: 'TKT-1008', subject: 'Calendar invites not syncing to Google Calendar',
    description: 'When I accept a meeting invite in Cruvzmail, it does not appear in my linked Google Calendar. The CalDAV sync appears to be set up correctly. This has been happening for the past 3 days.',
    category: 'technical', priority: 'low', status: 'closed',
    userId: 'u-demo', organizationId: 1, assignedTo: 'u-carol',
    requesterEmail: DEMO_EMAIL, requesterName: 'Demo User', isExternal: false,
    createdAt: DT10, updatedAt: DT7,
    userEmail: DEMO_EMAIL, userName: 'Demo User',
    assignedEmail: 'carol@cruvzmail.com', assignedName: 'Carol Williams',
    replyCount: 6,
  },
];
